import { useState, useEffect, useMemo } from 'react';
import { getAllConfigs } from '../services/configService';
import {
  DEFAULT_I18N_CONFIG,
  LANGUAGE_CODES,
  DEFAULT_LANGUAGE,
} from '../constants/languages';

/**
 * Normalize admin/public i18n config with safe defaults.
 * @param {object|null|undefined} raw
 */
export const normalizeI18nConfig = (raw) => {
  const base = { ...DEFAULT_I18N_CONFIG, ...(raw && typeof raw === 'object' ? raw : {}) };
  const list = Array.isArray(base.languagesInSwitcher)
    ? base.languagesInSwitcher.filter((c) => LANGUAGE_CODES.includes(c))
    : [...LANGUAGE_CODES];
  return {
    switcherEnabled: base.switcherEnabled !== false,
    defaultLanguage: LANGUAGE_CODES.includes(base.defaultLanguage)
      ? base.defaultLanguage
      : DEFAULT_LANGUAGE,
    languagesInSwitcher: list.length > 0 ? list : [DEFAULT_LANGUAGE],
  };
};

/**
 * Load storefront i18n settings from site config (switcher on/off + active langs).
 */
const useI18nConfig = () => {
  const [raw, setRaw] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getAllConfigs();
        if (cancelled) return;
        const data = response?.data || response || {};
        setRaw(data.i18n || null);
      } catch {
        // Fail closed: a config request that never answers must not leave the
        // switcher showing languages the shop has not turned on.
        if (!cancelled) setRaw({ switcherEnabled: false, languagesInSwitcher: [DEFAULT_LANGUAGE] });
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const config = useMemo(() => normalizeI18nConfig(raw), [raw]);

  return { config, loaded };
};

export default useI18nConfig;
